import { useState } from 'react';
import {
  importResultSchema,
  lineItemSchema,
  type ImportDomain,
  type ImportResult,
} from '@forecastify/shared';
import type { Toast } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { saveCsv } from '../exports/export-dialog';
import { ImportDialog } from './import-dialog';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ One line to give a screen an import. C17.                                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ The same shape as `useExport`, for the same reason: the mockup has one import arrow per list and
 * they all mean the same thing.
 */

let toastCounter = 0;

export interface ImportHandle {
  readonly dialog: React.ReactNode;
  readonly open: (domain: ImportDomain) => void;
  readonly toasts: readonly Toast[];
  readonly dismiss: (id: string | number) => void;
}

export function useImport(onDone?: () => void): ImportHandle {
  const session = useSession();
  const t = useT();

  const [domain, setDomain] = useState<ImportDomain | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  /**
   * P2-32.2 · every new task needs a line item. A row may name one in its `sortetel` column; the
   * rest take this one, chosen in the dialog before the file is dropped.
   */
  const [lineItems, setLineItems] = useState<readonly { id: string; label: string }[]>([]);
  const [defaultLineItemId, setDefaultLineItemId] = useState('');

  const toast = (entry: Omit<Toast, 'id'>): void => {
    toastCounter += 1;
    setToasts((current) => [...current, { ...entry, id: `import-${String(toastCounter)}` }]);
  };

  const upload = (filename: string, csv: string): void => {
    if (domain === null) return;
    void (async () => {
      setWorking(true);
      setFileError(null);
      setResult(null);

      const response = await session
        .request('/imports', {
          method: 'POST',
          body: JSON.stringify({
            domain,
            csv,
            filename,
            ...(domain === 'task' && defaultLineItemId !== '' ? { defaultLineItemId } : {}),
          }),
        })
        .catch(() => null);

      setWorking(false);
      if (response === null) {
        setFileError(t('error.unavailable'));
        return;
      }

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: { code?: string; details?: { message?: string }[] };
        } | null;
        const code = body?.error?.code ?? 'unavailable';

        /**
         * ⚠️⚠️ **A HEADER REFUSAL NAMES THE COLUMNS, because that is the one mistake worth naming.**
         * The server sends one detail per missing column; "the file is wrong" over a file somebody
         * cannot see what is wrong with is the message this avoids.
         */
        if (code === 'import_columns') {
          const columns = (body?.error?.details ?? [])
            .map((detail) => detail.message ?? '')
            .filter((name) => name !== '')
            .join(', ');
          setFileError(t('import.error.columns', { columns }));
          return;
        }
        if (code === 'import_too_large') {
          setFileError(
            t('import.error.tooLarge', { rows: body?.error?.details?.[0]?.message ?? '' }),
          );
          return;
        }
        setFileError(code === 'not_found' ? t('error.not_found') : t('error.unavailable'));
        return;
      }

      const parsed = importResultSchema.safeParse(await response.json());
      if (!parsed.success) {
        setFileError(t('error.unavailable'));
        return;
      }

      setResult(parsed.data);
      /* ⚠️ The caller reloads, because rows landed — the list behind the dialog is now stale. */
      onDone?.();
    })();
  };

  const sample = (): void => {
    if (domain === null) return;
    void (async () => {
      const response = await session.request(`/imports/sample?domain=${domain}`).catch(() => null);
      if (response?.ok !== true) {
        toast({ title: t('error.unavailable'), variant: 'error' });
        return;
      }
      const body = (await response.json()) as { filename?: string; csv?: string };
      if (body.filename === undefined || body.csv === undefined) return;
      saveCsv(body.filename, body.csv);
      toast({
        title: t('import.toast.sample'),
        description: t('import.toast.sampleNote'),
        variant: 'success',
      });
    })();
  };

  const reset = (): void => {
    setResult(null);
    setFileError(null);
  };

  return {
    dialog: (
      <ImportDialog
        domain={domain}
        result={result}
        fileError={fileError}
        working={working}
        onClose={() => {
          setDomain(null);
          reset();
        }}
        onUpload={upload}
        onSample={sample}
        onReset={reset}
        lineItems={lineItems}
        defaultLineItemId={defaultLineItemId}
        onDefaultLineItem={setDefaultLineItemId}
      />
    ),
    open: (next) => {
      reset();
      setDomain(next);
      setDefaultLineItemId('');
      if (next !== 'task') return;
      void (async () => {
        const response = await session.request('/line-items').catch(() => null);
        if (response?.ok !== true) return;
        const body = (await response.json().catch(() => null)) as { data?: unknown } | null;
        const parsed = lineItemSchema.array().safeParse(body?.data);
        if (!parsed.success) return;
        /* ⚠️ Only a line item that can hold work: one on a client contract has no project. */
        setLineItems(
          parsed.data
            .filter((item) => item.projectId !== null)
            .map((item) => ({
              id: item.id,
              /* R-57 · the line item's identifier, not `HSO-C01/2`. */
              label: `${item.identifier} · ${item.name}`,
            })),
        );
      })();
    },
    toasts,
    dismiss: (id) => {
      setToasts((current) => current.filter((entry) => entry.id !== id));
    },
  };
}
